/*
 * Edible Beauty cart — gift engine, cross-sells, quick-add and the optional saved-cart timer.
 * Rules come from window.cartRules (rendered by snippets/cart-config.liquid); the eligibility
 * maths mirrors snippets/cart-progress.liquid — keep both in sync.
 */
(function () {
  'use strict';

  const EVENTS = window.PUB_SUB_EVENTS;
  const GIFT_PROPERTY = '_eb_gift';

  /* ------------------------------------------------------------------ */
  /* Gifts: add when eligible, remove when not, one line per rule, qty 1 */
  /* ------------------------------------------------------------------ */
  const CartGifts = {

    rules() {
      return (window.cartRules && window.cartRules.gifts) || [];
    },

    excluded() {
      return new Set(((window.cartRules && window.cartRules.excludedHandles) || []).map((h) => String(h).trim()).filter(Boolean));
    },

    /* A line is a gift if we tagged it, or if it is one of the configured gift variants/handles
       (e.g. added by the previous cart before launch, or via a direct link). */
    isGiftLine(item) {
      if (item.properties && item.properties[GIFT_PROPERTY]) return true;
      return this.rules().some((rule) => this.matchesRule(item, rule));
    },

    matchesRule(item, rule) {
      if (item.properties && item.properties[GIFT_PROPERTY] === rule.id) return true;
      if (rule.variantId && item.variant_id === rule.variantId) return true;
      return !!(rule.handle && item.handle === rule.handle);
    },

    signature(cart) {
      const { total, handles } = this.qualifying(cart);
      return total + '|' + Array.from(handles).sort().join(',');
    },

    /* rule id -> cart signature at which that gift came back charged. In-memory only (per page view): it
       just stops an add/remove loop. Not persisted on purpose, so a misconfigured gift discount keeps
       showing up (console warning on every page load) until it is fixed in Shopify Admin. */
    suppressed: {},

    qualifying(cart) {
      const excluded = this.excluded();
      const handles = new Set();
      let total = 0;
      let lines = 0;
      (cart.items || []).forEach((item) => {
        if (this.isGiftLine(item)) return;
        handles.add(item.handle);
        if (excluded.has(item.handle)) return;
        total += item.final_line_price;
        lines += 1;
      });
      return { total, handles, lines };
    },

    plan(cart) {
      const { total, handles, lines: qualifyingLines } = this.qualifying(cart);
      const signature = this.signature(cart);
      const actions = [];
      this.rules().forEach((rule) => {
        if (!rule.enabled || !rule.autoAdd || !rule.variantId) return;
        /* A $0 spend threshold is a gift with every order: still needs one qualifying (non-excluded) item. */
        const threshold = Number(rule.threshold || 0);
        const eligible =
          rule.available &&
          (rule.type === 'product'
            ? (rule.triggerHandles || []).some((h) => handles.has(h))
            : threshold > 0 ? total >= threshold : qualifyingLines > 0);
        const lines = (cart.items || []).filter((item) => this.matchesRule(item, rule));

        if (!eligible) {
          delete this.suppressed[rule.id];
          lines.forEach((line) => actions.push({ type: 'remove', key: line.key, reason: 'ineligible' }));
          return;
        }

        /* Gift products are priced SKUs made free by the store's automatic discounts. If a gift
           line is still being charged, remove it and stop retrying until the cart changes, so a
           customer is never charged for a "free" gift. */
        const charged = lines.filter((line) => line.final_line_price > 0);
        if (charged.length) {
          charged.forEach((line) => actions.push({ type: 'remove', key: line.key, reason: 'charged' }));
          this.suppressed[rule.id] = signature;
          console.warn('[EB cart] gift is not discounted in this cart, removed it. Check its automatic discount in Shopify Admin:', rule.handle);
          return;
        }

        if (lines.length === 0) {
          if (this.suppressed[rule.id] !== signature) actions.push({ type: 'add', rule });
          return;
        }
        if (this.suppressed[rule.id] && this.suppressed[rule.id] !== signature) delete this.suppressed[rule.id];
        lines.slice(1).forEach((line) => actions.push({ type: 'remove', key: line.key }));
        if (lines[0].quantity > 1) actions.push({ type: 'set', key: lines[0].key });
      });
      return actions;
    },

    /* Send the planned gift actions straight to the Cart API (we are already inside EBCart's queue).
       Returns the sections from the last successful response, i.e. the cart after the final action. */
    run(actions) {
      const config = window.fetchConfig();
      const sections = window.EBCart.sectionNames();
      let latest = null;
      const strings = window.cartStrings || {};
      return actions
        .reduce((chain, action) => chain.then(() => {
          window.EBCart.setStatus(
            action.type === 'add' ? strings.giftAdding : action.reason === 'ineligible' ? strings.giftRemoving : strings.updating
          );
          let url;
          let body;
          if (action.type === 'add') {
            url = window.routes.cart_add_url;
            body = { items: [{ id: action.rule.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: action.rule.id } }] };
          } else {
            url = window.routes.cart_change_url;
            body = { id: action.key, quantity: action.type === 'set' ? 1 : 0 };
          }
          body.sections = sections;
          body.sections_url = window.location.pathname;
          const headers = Object.assign({}, config.headers, { [window.EBCart.ownHeader]: '1' });
          return fetch(url, Object.assign({}, config, { headers, body: JSON.stringify(body) }))
            .then((response) => response.json().then((json) => {
              if (!response.ok) throw new Error((json && (json.description || json.message)) || response.status);
              if (json && json.sections) latest = json.sections;
            }))
            .catch((err) => console.error('[EB cart] gift action failed', action.type, err));
        }), Promise.resolve())
        .then(() => latest);
    },

    /* EBCart resolver: runs on every confirmed cart before it is painted. Loops (max 3 rounds) so a gift
       that comes back charged is removed in the same pass and the customer never sees it. */
    resolve(result) {
      if (!result || !result.cart) return null;
      let current = result;
      let changed = false;
      const round = (n) => {
        const actions = n < 3 ? this.plan(current.cart) : [];
        if (!actions.length) return Promise.resolve();
        changed = true;
        return this.run(actions)
          .then((sections) => window.EBCart.fetchCart().then((cart) => {
            current = { cart, sections: sections || null };
          }))
          .then(() => round(n + 1));
      };
      return round(0).then(() => {
        if (!changed) return null;
        if (current.sections) return current;
        return window.EBCart.fetchSections().then((sections) => ({ cart: current.cart, sections }));
      });
    },

    /* A suppressed gift (in the cart but not discounted) must not show as "unlocked" in the progress bar,
       which Liquid renders without knowing the discount failed. Hide its stop after every render. */
    syncProgress() {
      const cart = window.EBCart.state.cart;
      const signature = cart ? this.signature(cart) : null;
      document.querySelectorAll('.cart-progress__stop[data-rule]').forEach((stop) => {
        stop.classList.toggle('cart-progress__stop--unavailable', !!signature && this.suppressed[stop.dataset.rule] === signature);
      });
    },

    init() {
      if (!this.rules().length) return;
      window.EBCart.addResolver((result) => this.resolve(result));
      document.addEventListener('cart:rendered', () => this.syncProgress());
      this.syncProgress();
      /* Page load: the server-rendered cart may already need a gift added or removed (e.g. a threshold
         crossed in another tab). Fix it through one queued refresh so it paints once. */
      const cart = window.EBCart.state.cart;
      if (cart && this.plan(cart).length) window.EBCart.refresh({ source: 'cart-gifts' }).catch(() => {});
    },
  };

  /* ------------------------------------------------------------------ */
  /* <cart-cross-sell> — fetches sections/cart-cross-sell.liquid         */
  /* ------------------------------------------------------------------ */
  /* Under 390px the heading collapses the cards. Collapsed by default; the shopper's choice lasts the
     browser session (the element is replaced on every drawer render, so state can't live on it). */
  const CROSS_SELL_SMALL = window.matchMedia('(max-width: 389px)');
  const CROSS_SELL_KEY = 'eb-cross-sell-open';
  /* Memory is the source of truth; storage only carries it to the next page and may be blocked. */
  let crossSellOpen = false;
  try { crossSellOpen = sessionStorage.getItem(CROSS_SELL_KEY) === '1'; } catch (e) { /* storage blocked */ }
  let crossSellUid = 0;

  class CartCrossSell extends HTMLElement {
    constructor() {
      super();
      this.onMediaChange = () => this.syncToggle();
      this.addEventListener('click', (event) => {
        if (!event.target.closest('.cart-cross-sell__toggle') || !CROSS_SELL_SMALL.matches) return;
        crossSellOpen = !crossSellOpen;
        try { sessionStorage.setItem(CROSS_SELL_KEY, crossSellOpen ? '1' : '0'); } catch (e) { /* storage blocked */ }
        this.syncToggle();
      });
    }

    connectedCallback() {
      this.unsubscribe = window.subscribe(EVENTS.cartUpdate, () => this.load());
      CROSS_SELL_SMALL.addEventListener('change', this.onMediaChange);
      this.load();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      CROSS_SELL_SMALL.removeEventListener('change', this.onMediaChange);
    }

    syncToggle() {
      const collapsed = CROSS_SELL_SMALL.matches && !crossSellOpen;
      this.classList.toggle('is-collapsed', collapsed);
      const toggle = this.querySelector('.cart-cross-sell__toggle');
      const body = this.querySelector('.cart-cross-sell__body');
      if (!toggle || !body) return;
      /* Collapsing only clips the cards; inert keeps their links and buttons out of Tab and screen readers. */
      body.inert = collapsed;
      if (!body.id) body.id = `CartCrossSell-${++crossSellUid}`;
      if (CROSS_SELL_SMALL.matches) {
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.setAttribute('aria-controls', body.id);
        toggle.removeAttribute('tabindex');
      } else {
        /* Wide screens: a plain heading, not a control. */
        toggle.removeAttribute('aria-expanded');
        toggle.removeAttribute('aria-controls');
        toggle.setAttribute('tabindex', '-1');
      }
    }

    buildUrl() {
      const cart = window.EBCart.state.cart;
      if (!cart || !cart.items || !cart.items.length) return null;
      if (this.dataset.source === 'collection') {
        const root = (window.routes.root_url || '/').replace(/\/$/, '');
        return `${root}/?section_id=cart-cross-sell`;
      }
      const seed = cart.items.find((item) => !(item.properties && item.properties[GIFT_PROPERTY])) || cart.items[0];
      return `${window.routes.product_recommendations_url}?section_id=cart-cross-sell&product_id=${seed.product_id}&limit=10&intent=related`;
    }

    load() {
      const url = this.buildUrl();
      if (!url) {
        this.hidden = true;
        return Promise.resolve();
      }
      /* A render + a cart-update can both ask for the same URL in one tick; fetch once. */
      if (this.inflightUrl === url && this.inflight) return this.inflight;
      const token = (this.token = {});
      this.inflightUrl = url;
      return (this.inflight = fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
        .then((text) => {
          if (token !== this.token) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const inner = doc.querySelector('.cart-cross-sell__inner');
          const count = inner ? Number(inner.dataset.count || 0) : 0;
          if (!inner || count === 0) {
            this.hidden = true;
            this.innerHTML = '';
            return;
          }
          this.innerHTML = inner.outerHTML;
          this.syncToggle();
          this.hidden = false;
        })
        .catch(() => {
          if (token === this.token) this.hidden = true;
        })
        .finally(() => {
          if (this.inflightUrl === url) {
            this.inflightUrl = null;
            this.inflight = null;
          }
        }));
    }
  }
  if (!customElements.get('cart-cross-sell')) customElements.define('cart-cross-sell', CartCrossSell);

  /* Quick-add buttons (cross-sell and anywhere else that uses data-cart-quick-add). */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;
    const variantId = Number(button.dataset.cartQuickAdd);
    if (!variantId) return;
    const label = button.textContent;
    const strings = window.cartStrings || {};
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = strings.adding || label;
    const onCartPage = !!document.getElementById('main-cart-items');
    /* Always a one-time purchase (no selling_plan): cross-sells never start a subscription. */
    window.EBCart.add([{ id: variantId, quantity: 1 }], { source: 'cross-sell', open: !onCartPage, trigger: button })
      .catch(() => {})
      .finally(() => {
        if (button.isConnected) {
          button.disabled = false;
          button.classList.remove('is-loading');
          button.textContent = label;
        }
      });
  });

  /* ------------------------------------------------------------------ */
  /* <cart-timer> — optional "your cart is saved for M:SS" countdown     */
  /* ------------------------------------------------------------------ */
  const TIMER_KEY = 'eb_cart_saved_time';
  class CartTimer extends HTMLElement {
    connectedCallback() {
      this.minutes = Number(this.dataset.minutes || 10);
      this.valueEl = this.querySelector('[data-cart-timer-value]');
      this.unsubscribe = window.subscribe(EVENTS.cartUpdate, (event) => {
        if (event && event.wasItemAdded && event.source !== 'cart-gifts') this.reset();
        this.tick();
      });
      if (this.dataset.active === 'true' && !this.start()) this.reset();
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      if (this.unsubscribe) this.unsubscribe();
    }

    start() {
      try { return Number(sessionStorage.getItem(TIMER_KEY) || 0); } catch (e) { return 0; }
    }

    reset() {
      try { sessionStorage.setItem(TIMER_KEY, String(Date.now())); } catch (e) { /* storage unavailable */ }
    }

    tick() {
      const cart = window.EBCart.state.cart;
      const start = this.start();
      let remaining = 0;
      if (cart && cart.item_count > 0 && start) remaining = Math.max(0, this.minutes * 60000 - (Date.now() - start));
      const m = Math.floor(remaining / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      if (this.valueEl) this.valueEl.textContent = `${m}:${String(s).padStart(2, '0')}`;
    }
  }
  if (!customElements.get('cart-timer')) customElements.define('cart-timer', CartTimer);

  CartGifts.init();
  window.EBCartGifts = CartGifts;
})();
